<template>
  <b-row>
    <b-col cols="12" md="6">
      <b-form-group v-if="planField" label-cols-sm="4">
        <template #label>
          {{ planFieldLabel }}
          <span v-if="planField.required">*</span>
          <span
            v-if="planField.description"
            v-b-popover.hover.top="planField.description"
          >
            <b-icon-question-circle variant="info" />
          </span>
        </template>
        <validation-provider
          v-slot="{ errors }"
          :rules="planValidationRules"
          :name="planFieldLabel"
          :vid="planField.key"
        >
          <b-form-checkbox-group
            v-model="selectedPlanIds"
            :options="planOptions"
            value-field="id"
            text-field="name"
            name="organisation-plans-selection"
            stacked
          />

          <p v-for="error in errors" :key="error" class="text-danger mb-0">
            {{ error }}
          </p>
        </validation-provider>

        <div v-if="selectedPlans.length > 0" class="organisation-plans-basket mt-3">
          <h6>{{ $t("selected_plans") }}</h6>
          <b-list-group>
            <b-list-group-item
              v-for="plan in selectedPlans"
              :key="plan.id"
              class="d-flex justify-content-between align-items-center"
            >
              <span>{{ getPlanLabel(plan) }}</span>
              <b-link
                :title="$t('remove')"
                :aria-label="$t('remove')"
                @click.stop.prevent="removePlan(plan.id)"
              >
                <b-icon-trash />
              </b-link>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between font-weight-bold">
              <span>{{ $t("total") }}</span>
              <span>{{ totalPriceLabel }}</span>
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-form-group>
    </b-col>

    <b-col cols="12" md="6">
      <form-field-input
        v-for="field in otherFields"
        :key="field.key"
        :field="field"
        :formConfig="formConfig"
        :input="input"
        @input="handleFieldInput"
      />
    </b-col>
  </b-row>
</template>

<script>
import _ from "lodash";
import { extend } from "vee-validate";
import { BIconQuestionCircle, BIconTrash } from "bootstrap-vue";
import moduleCommonMixin from "./moduleCommonMixin";
import { planMixin } from "@/shared";

const PLAN_FIELD_KEY = "PlanAssignment.organisationPlanId";

export default {
  components: {
    BIconQuestionCircle,
    BIconTrash,
  },
  mixins: [moduleCommonMixin, planMixin],
  props: {
    formConfig: {
      type: Object,
      required: true,
    },
    formModule: {
      type: Object,
      required: true,
    },
    input: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const existingValue = _.get(this.input, PLAN_FIELD_KEY);

    return {
      selectedPlanIds: Array.isArray(existingValue)
        ? [...existingValue]
        : existingValue
        ? [existingValue]
        : [],
    };
  },
  computed: {
    planField() {
      return this.getFormField(PLAN_FIELD_KEY);
    },
    planFieldLabel() {
      return this.planField.customLabel
        ? this.planField.customLabel
        : this.planField.label;
    },
    planValidationRules() {
      return this.planField.required ? "min-plans-1" : "";
    },
    plans() {
      return (this.planField && this.planField.options) || [];
    },
    planOptions() {
      return this.plans.map((plan) => ({
        id: plan.id,
        name: this.getPlanLabel(plan),
      }));
    },
    selectedPlans() {
      return this.plans.filter((plan) => this.selectedPlanIds.includes(plan.id));
    },
    totalPriceLabel() {
      const total = this.selectedPlans.reduce(
        (sum, plan) => sum + (this.getOrganisationPlanPrice(plan, this.formConfig) || 0),
        0
      );

      return this.formatOrganisationPlanPrice(total, this.formConfig.currencyCode);
    },
    otherFields() {
      return this.formModule.fields.filter(
        (x) => x.isVisible && x.key !== PLAN_FIELD_KEY
      );
    },
  },
  watch: {
    selectedPlanIds: {
      handler(value) {
        this.handleFieldInput({
          key: PLAN_FIELD_KEY,
          value: [...value],
        });
      },
      immediate: true,
    },
  },
  created() {
    // The global "required" rule treats an empty array as valid.
    // computesRequired makes vee-validate run this rule on an empty selection too.
    extend("min-plans-1", {
      message: () => `${this.$t("rules.select_at_least_one_plan")}`,
      validate: (value) => Array.isArray(value) && value.length > 0,
      computesRequired: true,
    });
  },
  methods: {
    getPlanLabel(plan) {
      return this.getOrganisationPlanLabel(plan, this.formConfig);
    },
    removePlan(planId) {
      this.selectedPlanIds = this.selectedPlanIds.filter((id) => id !== planId);
    },
  },
};
</script>
